import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChartLine, Clock, CheckCircle, ShieldCheck, ListChecks, ArrowsClockwise, Eye } from '@phosphor-icons/react';
import { useDashboard } from '@/hooks/useDashboard';
import { useCatalogs } from '@/hooks/useTasks';
import { useAppStore } from '@/store/appStore';
import { fmtDate } from '@/lib/utils';
import type { DashboardItem } from '@/types';

// Home de la app (F4 del feature de sprints). Tarjeta por colaborador con
// 3 KPIs del sprint elegido + indicador de horas + arrastradas del sprint
// anterior (heuristica server-side: FechaCreacion < FechaInicio del sprint).
//
// El sprint mostrado lo controla filters.sprintId del store (mismo que el
// selector del TopBar). null = "todos los sprints" -> caemos al semanal
// vigente para el dashboard (no tiene sentido sumar todos los sprints).
export function DashboardPage() {
  const navigate = useNavigate();
  const sprintIdFilter = useAppStore((s) => s.filters.sprintId);
  const setFilter = useAppStore((s) => s.setFilter);
  const { data: catalogs } = useCatalogs();

  // Cuando el TopBar dice "Todos los sprints" igual el dashboard agrega por
  // un sprint concreto — usamos el vigente como fallback (mismo orden que
  // el backend resuelve cuando no llega idSprint en el query string).
  const sprintIdToShow = sprintIdFilter;
  const { data, isLoading, refetch, isFetching } = useDashboard(sprintIdToShow);

  const items = data?.items ?? [];
  const today = new Date().toISOString().slice(0, 10);

  // Resumen para el header (totales del equipo).
  const totals = useMemo(() => {
    return items.reduce(
      (acc, it) => ({
        porTrabajar: acc.porTrabajar + it.porTrabajar,
        completadas: acc.completadas + it.completadas,
        sinAutorizar: acc.sinAutorizar + it.sinAutorizar,
        porRevisar: acc.porRevisar + it.porRevisar,
        arrastradas: acc.arrastradas + it.arrastradas,
        horasAsignadas: acc.horasAsignadas + it.horasAsignadas,
        horasEfectivas: acc.horasEfectivas + it.horasEfectivas,
        horasDisponibles: acc.horasDisponibles + it.horasDisponibles,
      }),
      { porTrabajar: 0, completadas: 0, sinAutorizar: 0, porRevisar: 0, arrastradas: 0, horasAsignadas: 0, horasEfectivas: 0, horasDisponibles: 0 }
    );
  }, [items]);

  // Click en una tarjeta -> pantalla de Tareas personales de esa persona
  // (Revisiones + Asignadas, agrupado por estado, con toggle Tabla/Kanban).
  function openPersonal(idCol: number) {
    navigate(`/personal/${idCol}`);
  }

  return (
    <div className="h-full flex flex-col bg-brand-bg overflow-hidden">
      <div className="px-6 py-4 border-b border-stone-200 bg-white flex items-center gap-4 flex-wrap">
        <h1 className="text-lg font-semibold text-stone-900 flex items-center gap-2">
          <ChartLine size={20} weight="bold" />
          Dashboard
        </h1>

        <div className="flex-1 min-w-[220px]">
          <select
            value={sprintIdFilter ?? ''}
            onChange={(e) => setFilter('sprintId', e.target.value ? Number(e.target.value) : null)}
            className="w-full max-w-[360px] px-3 py-1.5 border border-stone-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-green-100"
          >
            <option value="">— Sprint vigente —</option>
            {(catalogs?.sprints ?? []).map((s) => (
              <option key={s.idSprint} value={s.idSprint}>{s.nombre}</option>
            ))}
          </select>
        </div>

        {data && (
          <span className="text-xs text-stone-500 font-mono">
            {data.nombreSprint}
            {data.fechaInicio && data.fechaFin && (
              <> — {fmtDate(data.fechaInicio)} a {fmtDate(data.fechaFin)}</>
            )}
            {data.fechaInicio && data.fechaInicio.slice(0, 10) <= today
              && data.fechaFin && data.fechaFin.slice(0, 10) >= today && (
              <span className="ml-2 px-1.5 py-0.5 bg-brand-green-100/30 text-stone-900 rounded">
                Actual
              </span>
            )}
          </span>
        )}

        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          title="Actualizar"
          className="p-1.5 rounded hover:bg-stone-100 text-brand-gray-400 hover:text-brand-gray-500 disabled:opacity-50"
        >
          <ArrowsClockwise size={16} weight="bold" className={isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {/* Totales del equipo: banda con 5 contadores. Si arrastradas > 0
            sale en naranja como llamado de atencion. */}
        {!isLoading && items.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <TotalCard
              label="Por trabajar"
              value={totals.porTrabajar}
              icon={<ListChecks size={16} weight="bold" />}
              accent="text-stone-700"
            />
            <TotalCard
              label="Completadas"
              value={totals.completadas}
              icon={<CheckCircle size={16} weight="bold" />}
              accent="text-emerald-700"
            />
            <TotalCard
              label="Sin autorizar"
              value={totals.sinAutorizar}
              icon={<ShieldCheck size={16} weight="bold" />}
              accent="text-amber-600"
            />
            <TotalCard
              label="Por revisar"
              value={totals.porRevisar}
              icon={<Eye size={16} weight="bold" />}
              accent="text-blue-700"
            />
            <TotalCard
              label={`Horas del equipo (asignadas / disponibles)`}
              value={`${totals.horasAsignadas.toFixed(1)} / ${totals.horasDisponibles.toFixed(0)}`}
              icon={<Clock size={16} weight="bold" />}
              accent="text-stone-700"
            />
          </div>
        )}

        {/* Si hay arrastradas, banda destacada arriba — el lunes 06:00 el
            Timer Function moves not-completed tasks al sprint nuevo (F2),
            y ese set deberia ser lo primero que se vea. */}
        {!isLoading && totals.arrastradas > 0 && (
          <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-md px-4 py-3 flex items-center gap-3">
            <ArrowsClockwise size={18} weight="bold" className="text-amber-700 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">
                {totals.arrastradas} tarea{totals.arrastradas !== 1 ? 's' : ''} arrastrada{totals.arrastradas !== 1 ? 's' : ''} del sprint anterior
              </p>
              <p className="text-xs text-amber-800/80">
                Fueron creadas antes del lunes y siguen sin completarse. Revisalas primero.
              </p>
            </div>
          </div>
        )}

        {isLoading && (
          <div className="text-center text-stone-500 py-12">Cargando dashboard…</div>
        )}

        {!isLoading && items.length === 0 && (
          <div className="text-center text-stone-500 italic py-12">
            Sin colaboradores activos.
          </div>
        )}

        {!isLoading && items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((it) => (
              <UserCard
                key={it.idCol}
                item={it}
                onClick={() => openPersonal(it.idCol)}
                onRevisar={() => openPersonal(it.idCol)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TotalCard({
  label, value, icon, accent,
}: {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  accent: string;
}) {
  return (
    <div className="bg-white border border-stone-200 rounded-md px-4 py-3 flex flex-col gap-1">
      <span className={`text-xs uppercase tracking-wide font-medium flex items-center gap-1.5 ${accent}`}>
        {icon}
        {label}
      </span>
      <span className="text-2xl font-bold text-stone-900 font-mono">{value}</span>
    </div>
  );
}

function UserCard({ item, onClick, onRevisar }: { item: DashboardItem; onClick: () => void; onRevisar: () => void }) {
  const ratio = item.horasDisponibles > 0 ? item.horasAsignadas / item.horasDisponibles : (item.horasAsignadas > 0 ? 1 : 0);
  const ratioClamped = Math.min(ratio, 1);
  const isOver = ratio >= 1;
  const isWarn = ratio >= 0.8 && !isOver;
  const fillColor = isOver ? '#BB4A4A' : isWarn ? '#F0C802' : '#ADD010';

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-white border border-stone-200 rounded-md p-4 hover:shadow-md hover:border-brand-green-100 transition-all flex flex-col gap-3 cursor-pointer"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-stone-900 truncate">{item.nombreCompleto || '—'}</h3>
        <div className="flex items-center gap-1.5 shrink-0">
          {/* El "por revisar" se movió al KPI Revisor (más jerarquía); acá
              arriba queda solo el badge de arrastradas. */}
          {item.arrastradas > 0 && (
            <span
              title={`${item.arrastradas} arrastrada${item.arrastradas !== 1 ? 's' : ''} del sprint anterior`}
              className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-900 bg-amber-100 border border-amber-200 rounded-full px-1.5 py-0.5"
            >
              <ArrowsClockwise size={10} weight="bold" />
              {item.arrastradas}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <Kpi
          label="Por trabajar"
          value={item.porTrabajar}
          color="text-stone-700"
        />
        <Kpi
          label="Completadas"
          value={item.completadas}
          color="text-emerald-700"
        />
        <Kpi
          label="Sin autorizar"
          value={item.sinAutorizar}
          color="text-amber-600"
        />
        {/* Revisor: ratio "pendientes por revisar / total asignadas como
            revisor" (ej. 6/10). Cuando hay pendientes (>0) el número va en azul
            y es clickeable → kanban filtrado por ese autorizador (acceso
            rápido). Sin pendientes queda gris. */}
        <div className="flex flex-col items-start gap-0.5">
          {item.porRevisar > 0 ? (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onRevisar(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onRevisar(); } }}
              title={`${item.porRevisar} por revisar de ${item.comoRevisor} asignada${item.comoRevisor !== 1 ? 's' : ''} como revisor — ir al kanban`}
              className="inline-flex items-center gap-1 text-2xl font-bold font-mono text-blue-700 cursor-pointer hover:underline"
            >
              <Eye size={16} weight="bold" />
              {item.porRevisar}/{item.comoRevisor}
            </span>
          ) : (
            <span
              className="text-2xl font-bold font-mono text-stone-400"
              title={`0 por revisar de ${item.comoRevisor} asignada${item.comoRevisor !== 1 ? 's' : ''} como revisor`}
            >
              {item.porRevisar}/{item.comoRevisor}
            </span>
          )}
          <span className="text-[10px] uppercase tracking-wide text-stone-500">Por revisar / asignadas</span>
        </div>
      </div>

      {item.horasEfectivas > 0 && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between text-[11px] font-medium text-stone-600">
            <span
              className="flex items-center gap-1"
              title={`Bruta ${item.horasEfectivas.toFixed(1)}h − revisión ${item.horasRevision.toFixed(1)}h = disponible ${item.horasDisponibles.toFixed(1)}h`}
            >
              <Clock size={11} weight="bold" />
              {item.horasAsignadas.toFixed(1)} / {item.horasDisponibles.toFixed(0)} h
              {item.tieneOverride && (
                <span title="Override de sprint activo" className="text-amber-600 font-bold">*</span>
              )}
            </span>
            <span className={isOver ? 'text-red-700 font-semibold' : isWarn ? 'text-amber-700' : 'text-stone-500'}>
              {(ratio * 100).toFixed(0)}%
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-stone-200 overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${ratioClamped * 100}%`, background: fillColor }} />
          </div>
        </div>
      )}
    </button>
  );
}

function Kpi({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="flex flex-col items-start gap-0.5">
      <span className={`text-2xl font-bold font-mono ${color}`}>{value}</span>
      <span className="text-[10px] uppercase tracking-wide text-stone-500">{label}</span>
    </div>
  );
}
